import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import { rgba } from 'polished';
import { useState } from 'react';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import AwardIcon from 'packages/kakapo-ui-kit/assets/icons/AwardIcon';
import EmptyStateCard from 'packages/kakapo-ui-kit/components/cards/EmptyStateCard';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import { useWorkspace } from 'packages/kakapo-ui-services/hooks/useWorkspace';
import { useKeychain } from '@treams/auth';

const AnchorElement = styled.span`
	color: ${(props) => props.theme.colors.primary};
	text-decoration: underline;
	cursor: pointer;
`;

const IconWrapper = styled.div<{ $color: string }>`
	border-radius: 50%;
	border: 1px solid ${(props) => rgba(props.$color, 0.2)};
	width: 48px;
	height: 48px;
	padding-top: 11px;

	svg {
		display: block;
		margin: 0 auto;

		path {
			fill: ${(props) => props.$color};
		}
	}
`;

const EventTitle = styled(ParagraphSmall)`
	font-weight: bold;
`;

function LearningCertificateListItem({
	certificate,
}: {
	certificate: graphql.LearningAwardedCertificate;
}) {
	const keychain = useKeychain();
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const workspace = useWorkspace();

	const [loading, setLoading] = useState(false);

	const learningSubjects = certificate.event.learningSubjects.map(
		(subject) => localize(subject.title).text,
	);

	const download = async () => {
		setLoading(true);

		try {
			const response = await fetch(
				`${import.meta.env.VITE_API ?? 'https://api.ontreams.com'}/learning/certificates/${
					certificate.id
				}`,
				{
					headers: {
						'X-Treams-Workspace': workspace.id,
						Authorization: `Bearer ${keychain.all()[0].accessToken}`,
					},
				},
			);

			const blob = await response.blob();

			if (blob) {
				const link = document.createElement('a');
				const url = URL.createObjectURL(blob);

				link.href = url;
				link.download = `Certificate ${localize(certificate.event.title).text}.pdf`;
				document.body.appendChild(link);
				link.click();

				URL.revokeObjectURL(url);
				document.body.removeChild(link);
			}
		} catch (err) {
			setLoading(false);
		}

		setLoading(false);
	};

	return (
		<>
			<Box my={3}>
				<Flex alignItems="center">
					<Box ml={2}>
						<IconWrapper $color={certificate.event.learningType.color}>
							<AwardIcon width={25} height={26} />
						</IconWrapper>
					</Box>
					<Box flexGrow={1} ml={3}>
						<Flex flexDirection="column" justifyContent="center" alignItems="flex-start">
							<EventTitle color="slateBlue">{localize(certificate.event.title).text}</EventTitle>
							<ParagraphSmall color="slateBlue">
								{moment(certificate.awardedAt).format('MMM YYYY')} &mdash;{' '}
								{localize(certificate.event.learningType.title).text}
								{learningSubjects.length > 0 ? ` — ${learningSubjects.join(', ')}` : ''}
							</ParagraphSmall>
						</Flex>
					</Box>
					<Box ml={3}>
						{loading ? (
							<Spinner isLoading verticalMargin={0} />
						) : (
							<AnchorElement onClick={() => download()}>
								{t('learning.certificates.downloadPdf')}
							</AnchorElement>
						)}
					</Box>
				</Flex>
			</Box>
			<HorizontalRule />
		</>
	);
}

export default function LearningCertificatesList({
	certificates,
}: {
	certificates: graphql.LearningAwardedCertificate[];
}) {
	const { t } = useTranslation();

	return (
		<>
			{certificates.length > 0 ? (
				certificates.map((certificate) => (
					<LearningCertificateListItem key={certificate.id} certificate={certificate} />
				))
			) : (
				<EmptyStateCard title={t('learning.certificates.empty')} />
			)}
		</>
	);
}
